import { chain } from 'lodash';
import { DateTime } from 'luxon';

import {
  ValidationDataDto,
  BenefitSalaryDto,
  FieldValueDto,
  ExtendedFieldWithValueDto,
  FormFieldsCategory,
  FormField,
  Option,
  FieldValue,
  BenefitSalary,
  FieldDataType,
  PrimitiveFieldValue,
  InputType,
  FormValues,
  FormValuesDto,
  SubscriberResponseDto,
} from 'theSource/types';
import { BENEFIT_SALARY_FIELD_NAMES } from './constants';
import { SIN_FIELD_NAME, SSN_FIELD_NAME } from '../constants';
import { convertApiDateToDateTime, convertToApiDateString } from '../dateUtils';
import {
  getFieldCategory,
  mapFields,
} from 'theSource/features/FamilyMembers/utils';
import { QMCSO_BENEFITS_FIELDS } from 'theSource/features/FamilyMembers/components/FamilyMemberForm/utils/constants';

export const isBenefitSalaryField = (fieldName: string) =>
  BENEFIT_SALARY_FIELD_NAMES.includes(fieldName);

const isMasked = (value: string) => /^XXX-XX-/.test(value);

export const isMaskedSocialNumberField = (fieldName: string, value: string) =>
  (fieldName === SSN_FIELD_NAME || fieldName === SIN_FIELD_NAME) &&
  isMasked(value);

export const mapOptions = (data: ValidationDataDto): Option[] => {
  return (
    data?.map<Option>((optionData) => ({
      key: optionData[0],
      label: optionData[0],
      value: optionData[1],
    })) ?? []
  );
};

export const normalizeValue = (
  value: PrimitiveFieldValue,
  dataType: FieldDataType
) => {
  switch (dataType) {
    case 'BOOLEAN':
      return value ?? false;
    case 'DATE':
      return convertApiDateToDateTime(value as string);
    default:
      return value ?? '';
  }
};

const mapBenefitSalaryValue = (value: BenefitSalaryDto[]): BenefitSalary[] =>
  value
    .map((benefitSalary) => ({
      benefitSalary: benefitSalary.benefit_salary,
      orgPlanYearId: benefitSalary.org_plan_year_id,
      orgPlanYearName: benefitSalary.org_plan_year_name,
    }))
    .sort((benefitSalary1, benefitSalary2) =>
      benefitSalary2.orgPlanYearName.localeCompare(
        benefitSalary1.orgPlanYearName
      )
    );

export const mapSubscriberModel = (
  response: SubscriberResponseDto,
  originalFormFields: { [key: string]: string | null }
): FormFieldsCategory[] => {
  // only include fields defined in FORM_FIELDS
  const mappedFields = Object.entries(response.data)
    .filter(([key, _]) => key in originalFormFields)
    .map<ExtendedFieldWithValueDto>(([key, value]) => ({
      fieldName: key,
      ...value,
    }));

  // group by categories and map data
  const categories = mapCategories(mappedFields, originalFormFields);

  return categories;
};

export const mapBeneficiaryModel = (
  metadata: any,
  values?: { [key: string]: any }
) => {
  const fields = mapFields(metadata);

  const mappedFields: ExtendedFieldWithValueDto[] = [];
  fields.forEach((metadata) => {
    mappedFields.push({
      fieldName: metadata.fieldName,
      metadata: {
        ...metadata,
        category: getFieldCategory(metadata.fieldName, metadata.category),
      },
      value: values ? values[metadata.fieldName] || null : null,
    });
  });

  const categories = mapCategories(mappedFields);
  return categories;
};

const mapFieldValue = (
  fieldName: string,
  value: FieldValueDto,
  dataType: FieldDataType
): FieldValue => {
  if (isBenefitSalaryField(fieldName)) {
    return value ? mapBenefitSalaryValue(value as BenefitSalaryDto[]) : [];
  }
  return normalizeValue(value as PrimitiveFieldValue, dataType);
};

export const mapDataType = (dataType: string | null): FieldDataType => {
  if (dataType) {
    return dataType as FieldDataType;
  }
  return 'TEXT';
};

const mapField = (
  data: ExtendedFieldWithValueDto,
  formFields?: { [key: string]: string | null }
): FormField => {
  const dataType = mapDataType(data.metadata.data_type);
  const fieldName = data.fieldName;
  let inputType;
  if (formFields) {
    inputType =
      (formFields[fieldName as keyof typeof formFields] as InputType) ??
      undefined;
  }

  return {
    dataType,
    // Currently we display benefitSalary field only in readonly mode
    editable: data.metadata.update && !isBenefitSalaryField(fieldName),
    fieldName,
    inputType,
    label: data.metadata.caption,
    maxLength: data.metadata.max_length ?? undefined,
    options: mapOptions(data.metadata.validation_data),
    order: data.metadata.order ?? Number.MAX_VALUE,
    required: data.metadata.required,
    value: mapFieldValue(fieldName, data.value, dataType),
  };
};

export const mapCategories = (
  mappedFields: ExtendedFieldWithValueDto[],
  originalFormFields?: { [key: string]: string | null }
) =>
  chain(mappedFields)
    .filter((field) => Boolean(field.metadata.category))
    .groupBy((field) => field.metadata.category)
    .map<FormFieldsCategory>((group, key) => ({
      name: key,
      order: group[0].metadata.category_order ?? Number.MAX_VALUE,
      fields: chain(group)
        .map((data) => mapField(data, originalFormFields))
        .orderBy((field) => field.order, 'asc')
        .value(),
    }))
    .orderBy((category) => category.order, 'asc')
    .value();

const mapBenefitSalary = (
  benefitSalaries: BenefitSalary[]
): BenefitSalaryDto[] =>
  benefitSalaries.map((value) => ({
    benefit_salary: value.benefitSalary,
    org_plan_year_id: value.orgPlanYearId,
    org_plan_year_name: value.orgPlanYearName,
  }));

export const mapFormValues = (
  categories: FormFieldsCategory[],
  values: FormValues
): FormValuesDto => {
  const fields = categories.flatMap((category) => category.fields);
  const data: { [fieldName: string]: FieldValueDto } = {};
  Object.entries(values).forEach(([fieldName, value]) => {
    const field = fields.find((field) => field.fieldName === fieldName);

    if (!field) {
      throw new Error(`Field ${fieldName} does not exist`);
    }

    if (isBenefitSalaryField(fieldName)) {
      // temporarily commenting out as currently editing benefit salaries does not work
      // and is causing issues when attempting to save the form.
      // this will be addressed in a future ticket.
      // data[fieldName] = mapBenefitSalary(value as BenefitSalary[]);
      return;
    }

    if (field.dataType === 'DATE') {
      if (value instanceof DateTime) {
        data[fieldName] = convertToApiDateString(value);
      } else {
        data[fieldName] = value as FieldValueDto;
      }
      return;
    }

    if (isMaskedSocialNumberField(fieldName, value as string)) {
      return;
    }

    if (QMCSO_BENEFITS_FIELDS.includes(field.fieldName)) {
      return;
    }

    data[fieldName] = value as FieldValueDto;
  });

  return data;
};
